import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download, Scale } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { VerifyChip } from "../components/DataLabel";
import { ShareCard } from "../components/ShareCard";
import { LandLead } from "../components/LandLead";
import { LandLeadMap } from "../components/LandLeadMap";
import { LandAlerts } from "../components/LandAlerts";
import { SavedComparisons } from "../components/SavedComparisons";
import { StartAgain } from "../components/StartAgain";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ksh = (n) => (n ? `KSh ${Math.round(n).toLocaleString()}` : "Nothing");
const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

/** The same acreage priced six ways, for one county or two side by side. */
export default function LeaseComparison() {
  const [params, setParams] = useSearchParams();
  const [counties, setCounties] = useState([]);
  const [county, setCounty] = useState(params.get("county") || "Nairobi");
  const [countyB, setCountyB] = useState(params.get("county_b") || "");
  const [acres, setAcres] = useState(params.get("acres") || "5");
  const [location, setLocation] = useState(params.get("location") || "peri_urban");
  const [data, setData] = useState(null);
  const [rankTerm, setRankTerm] = useState("freehold");
  const [ranking, setRanking] = useState(null);
  const [afford, setAfford] = useState(null);

  useSeo({
    title: "Lease Comparison — Own It, Lease It Or Share It — KickStartKenyaFootball",
    description:
      "Any county's land priced six ways side by side — freehold, 99, 50 and 15-year leases, revenue share and a public-interest grant of use — so a county, a club or a funding partner can see the cheapest route in.",
  });

  useEffect(() => {
    api.get("/counties")
      .then((r) => {
        const rows = Array.isArray(r.data) ? r.data : r.data.counties || [];
        setCounties(rows.map((c) => c.county || c.name).filter(Boolean));
      })
      .catch(() => {});
  }, []);

  // Only the parameters that carry meaning, so a link sent to a funder is clean.
  const query = new URLSearchParams({
    county,
    ...(countyB ? { county_b: countyB } : {}),
    acres: String(parseFloat(acres || "0") || 0),
    location,
  }).toString();

  useEffect(() => {
    api.get(`/lease-comparison?${query}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [query]);

  useEffect(() => {
    api.get(`/lease-ranking?acres=${parseFloat(acres || "0") || 0}&location=${location}&term=${rankTerm}`)
      .then((r) => setRanking(r.data))
      .catch(() => setRanking(null));
  }, [acres, location, rankTerm]);

  useEffect(() => {
    api.get(`/lease-affordability?county=${encodeURIComponent(county)}&location=${location}`)
      .then((r) => setAfford(r.data))
      .catch(() => setAfford(null));
  }, [county, location]);

  // Keep the address bar in step so the page can be sent to a funder exactly as it is being read. The hash is
  // remembered first: rewriting the query drops it, and the section it points at only exists once data lands.
  const wantedHash = useRef(typeof window === "undefined" ? "" : window.location.hash);
  useEffect(() => { setParams(new URLSearchParams(query), { replace: true }); }, [query, setParams]);

  useEffect(() => {
    if (!data || !wantedHash.current || wantedHash.current.length < 2) return;
    const el = document.querySelector(wantedHash.current);
    if (!el) return;
    const target = wantedHash.current;
    wantedHash.current = "";
    const go = () => document.querySelector(target)?.scrollIntoView({ behavior: "auto", block: "start" });
    requestAnimationFrame(go);
    // the page grows as the map and tables finish laying out, so the position is taken again once it settles
    setTimeout(go, 600);
    setTimeout(go, 1400);
  }, [data]);

  if (!data) return <div className="min-h-[60vh] bg-black" data-testid="lease-comparison-loading" />;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="lease-comparison-page">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <p className="overline text-ochre">{data.heading}</p>
          <h1 className="display-1 mt-5 max-w-4xl">{data.sub}</h1>
          <p className="body-copy mt-6 max-w-3xl">{data.intro}</p>
          <div className="mono mt-8 flex max-w-3xl items-start gap-3 border-l-2 border-maasai bg-[#0b0b0b] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400">
            <Scale size={16} className="mt-0.5 shrink-0 text-maasai" />
            <span><VerifyChip v="estimate" /> {data.caveat}</span>
          </div>
        </Reveal>

        <section className="mt-14" data-testid="lease-controls">
          <p className="eyebrow">Your site</p>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <h2 className="display-3">Price It Where You Are</h2>
            <StartAgain label="Clear my selections" to="/lease-comparison" testId="lease-start-again" />
          </div>
          <div className="mt-7 flex flex-wrap items-end gap-4">
            <label className="block">
              <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">County</span>
              <select
                value={county}
                onChange={(e) => {
                  if (e.target.value === countyB) setCountyB(county);
                  setCounty(e.target.value);
                }}
                data-testid="lease-county-select"
                className="mt-2 border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none"
              >
                {counties.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">Compare with</span>
              <select
                value={countyB}
                onChange={(e) => setCountyB(e.target.value)}
                data-testid="lease-county-b-select"
                className="mt-2 border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none"
              >
                <option value="">No second county</option>
                {counties.filter((c) => c !== county).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">Acres</span>
              <input
                value={acres}
                onChange={(e) => setAcres(e.target.value.replace(/[^\d.]/g, ""))}
                inputMode="decimal"
                data-testid="lease-acres-input"
                className="mt-2 w-32 border border-white/20 bg-transparent px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">Where the land is</span>
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                data-testid="lease-location-select"
                className="mt-2 border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none"
              >
                {data.locations.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
              </select>
            </label>
            <a
              href={`${BACKEND}/api/lease-comparison.pdf?${query}`}
              target="_blank"
              rel="noreferrer"
              data-testid="lease-pdf-link"
              className="mono mb-0.5 inline-flex items-center gap-2 border border-ochre px-4 py-2.5 text-[0.55rem] uppercase tracking-[0.16em] text-ochre transition-colors hover:bg-ochre hover:text-black"
            >
              <Download size={13} /> Comparison PDF
            </a>
          </div>
          <p className="mono mt-4 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500" data-testid="lease-plot-note">
            {data.acres} acres · {Math.round(data.square_metres).toLocaleString()} m² · {data.location_label}
          </p>
        </section>

        {data.columns.map((col) => {
          const cheapest = col.rows.find((r) => r.term === col.cheapest_over_15);
          return (
            <section key={col.county} className="mt-20" data-testid={`lease-column-${col.county.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="eyebrow">{col.county} County</p>
              <h2 className="display-3 mt-4">Six Routes Into The Same {data.acres} Acres</h2>
              <p className="mt-5 max-w-3xl text-sm leading-relaxed text-zinc-400">
                Land here runs at <span className="text-white">{ksh(col.per_acre_shillings)}</span> an acre on our
                model (county index {col.cost_index}, land index {col.land_index}).
                {data.acres > 0 ? (
                  <> So the freehold is <span className="text-white">{ksh(col.freehold_shillings)}</span> and ground
                    rent is charged at {col.ground_yield_pct}% of that a year.</>
                ) : <> Enter an acreage above to price the six routes.</>}
                {cheapest ? (
                  <> The cheapest paid way in over {data.horizon_years} years is the{" "}
                    <span className="text-ochre">{cheapest.term_label.toLowerCase()}</span>.</>
                ) : null}
              </p>

              <div className="mt-8 overflow-x-auto border border-zinc-900">
                <table className="w-full min-w-[900px] text-left" data-testid={`lease-table-${col.county.toLowerCase().replace(/\s+/g, "-")}`}>
                  <thead>
                    <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                      {["Route", "Day one", "Rent a year", `Cheapest over ${data.horizon_years} years`, "Share of freehold"].map((h) => (
                        <th key={h} className="mono px-4 py-3 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-300">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {col.rows.map((r) => {
                      const best = r.term === col.cheapest_over_15;
                      return (
                        <tr
                          key={r.term}
                          className={`border-b border-zinc-900 align-top ${best ? "bg-[#120b06]" : ""}`}
                          data-testid={`lease-row-${r.term}`}
                        >
                          <td className="px-4 py-5">
                            <span className="block font-display text-base uppercase leading-tight text-white">{r.term_label}</span>
                            <span className="mt-1.5 block text-xs leading-relaxed text-zinc-500">{r.note}</span>
                            {best ? (
                              <span className="mono mt-2 inline-block bg-ochre px-2 py-1 text-[0.65rem] uppercase tracking-[0.16em] text-black" data-testid={`lease-cheapest-${r.term}`}>
                                Cheapest paid route
                              </span>
                            ) : null}
                          </td>
                          <td className="mono px-4 py-5 text-xs text-white">{ksh(r.day_one_shillings)}</td>
                          <td className="mono px-4 py-5 text-xs text-zinc-300">
                            {r.pays_rent ? ksh(r.rent_year_shillings) : "No rent"}
                            {r.pays_rent ? (
                              <span className="mt-1 block text-[0.6rem] tracking-[0.14em] text-zinc-400">
                                {ksh(r.rent_month_shillings)} a month
                              </span>
                            ) : null}
                          </td>
                          <td className="mono px-4 py-5 text-xs text-ochre">
                            {ksh(r.cheapest_15_shillings)}
                            {r.pays_rent ? (
                              <span className="mt-1 block text-[0.65rem] normal-case tracking-normal text-zinc-400">
                                premium {ksh(r.premium_route_15_shillings)} or rent {ksh(r.rent_route_15_shillings)}
                              </span>
                            ) : null}
                          </td>
                          <td className="mono px-4 py-5 text-xs text-zinc-300">{r.premium_share_of_freehold_pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })}

        <section className="mt-20" data-testid="lease-ranking">
          <p className="eyebrow">All 47 counties</p>
          <h2 className="display-3 mt-4">{ranking ? ranking.heading : "Where The Same Acre Costs Least"}</h2>
          <div className="mt-7 flex flex-wrap items-end gap-4">
            <label className="block">
              <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">Route to price</span>
              <select
                value={rankTerm}
                onChange={(e) => setRankTerm(e.target.value)}
                data-testid="lease-rank-term-select"
                className="mt-2 border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none"
              >
                {data.terms.filter((t) => !["revenue_share", "donation"].includes(t.id))
                  .map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </label>
            {ranking ? (
              <p className="mono pb-3 text-[0.55rem] uppercase tracking-[0.16em] text-ochre" data-testid="lease-rank-summary">
                Cheapest {ranking.cheapest} · dearest {ranking.dearest}
              </p>
            ) : null}
          </div>
          {ranking ? (
            <>
              <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">{ranking.intro}</p>
              <div className="mt-8 overflow-x-auto border border-zinc-900">
                <table className="w-full min-w-[760px] text-left" data-testid="lease-rank-table">
                  <thead>
                    <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                      {["#", "County", "Per acre", "Day one", "Rent a year", "Cost index"].map((h) => (
                        <th key={h} className="mono px-4 py-3 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-300">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ranking.rows.map((r) => (
                      <tr
                        key={r.county}
                        className={`border-b border-zinc-900 ${r.county === county || r.county === countyB ? "bg-[#120b06]" : ""}`}
                        data-testid={`lease-rank-row-${r.county.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <td className="mono px-4 py-3.5 text-xs text-maasai">{String(r.rank).padStart(2, "0")}</td>
                        <td className="px-4 py-3.5">
                          <button
                            type="button"
                            onClick={() => {
                              if (r.county === countyB) setCountyB(county);
                              setCounty(r.county);
                            }}
                            className="font-display text-sm uppercase tracking-tight text-white hover:text-ochre"
                            data-testid={`lease-rank-pick-${r.county.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {r.county}
                          </button>
                        </td>
                        <td className="mono px-4 py-3.5 text-xs text-zinc-300">{ksh(r.per_acre_shillings)}</td>
                        <td className="mono px-4 py-3.5 text-xs text-ochre">{ksh(r.day_one_shillings)}</td>
                        <td className="mono px-4 py-3.5 text-xs text-zinc-300">
                          {r.rent_year_shillings ? ksh(r.rent_year_shillings) : "No rent"}
                        </td>
                        <td className="mono px-4 py-3.5 text-xs text-zinc-400">{r.cost_index}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </section>

        <section className="mt-20" data-testid="lease-affordability">
          <p className="eyebrow">Can the facility pay for it</p>
          <h2 className="display-3 mt-4">{afford ? afford.heading : "Can It Carry The Rent"}</h2>
          {afford ? (
            <>
              <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">{afford.intro}</p>
              <p className="mono mt-4 text-[0.6rem] uppercase tracking-[0.16em] text-ochre" data-testid="lease-afford-summary">
                {afford.carries_count} of {afford.rows.length} types carry a market ground rent in {county}
              </p>
              <div className="mt-8 overflow-x-auto border border-zinc-900">
                <table className="w-full min-w-[900px] text-left" data-testid="lease-afford-table">
                  <thead>
                    <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                      {["Facility type", "Land it needs", "Rent a year", "Share of income", "Share of profit", "Verdict"].map((h) => (
                        <th key={h} className="mono px-4 py-3 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-300">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {afford.rows.map((r) => (
                      <tr key={r.facility} className="border-b border-zinc-900 align-top" data-testid={`lease-afford-row-${r.facility}`}>
                        <td className="px-4 py-5">
                          <span className="block font-display text-sm uppercase leading-tight text-white">{r.name}</span>
                          <span className="mono mt-1 block text-[0.6rem] uppercase tracking-[0.14em] text-zinc-400">
                            {r.tier_label} · {r.funding_label}
                          </span>
                        </td>
                        <td className="mono px-4 py-5 text-xs text-zinc-300">
                          {r.land_acres} acres
                          <span className="mt-1 block text-[0.6rem] tracking-[0.14em] text-zinc-400">
                            {Math.round(r.land_m2).toLocaleString()} m²
                          </span>
                        </td>
                        <td className="mono px-4 py-5 text-xs text-zinc-300">
                          {ksh(r.rent_year_shillings)}
                          <span className="mt-1 block text-[0.6rem] tracking-[0.14em] text-zinc-400">
                            {ksh(r.rent_month_shillings)} a month
                          </span>
                        </td>
                        <td className="mono px-4 py-5 text-sm text-ochre">{r.rent_share_of_income_pct}%</td>
                        <td className="mono px-4 py-5 text-xs text-zinc-300">
                          {r.rent_share_of_profit_pct === null ? "No profit to take it from" : `${r.rent_share_of_profit_pct}%`}
                        </td>
                        <td className="px-4 py-5">
                          <span className={`font-display text-sm uppercase leading-tight ${r.carries_the_rent ? "text-white" : "text-maasai"}`}>
                            {r.verdict}
                          </span>
                          <span className="mt-1.5 block text-xs leading-relaxed text-zinc-500">{r.verdict_note}</span>
                          <a
                            href={`${BACKEND}/api/rent-sheet.pdf?county=${encodeURIComponent(county)}&location=${location}&facility=${r.facility}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`rent-sheet-${r.facility}`}
                            className="mono mt-3 inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.16em] text-ochre hover:text-white"
                          >
                            <Download size={11} /> Rent negotiation sheet
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-5 max-w-3xl text-xs leading-relaxed text-zinc-500">{afford.method}</p>
            </>
          ) : null}
        </section>

        <section className="mt-20" data-testid="lease-share">
          <p className="eyebrow">Send it on</p>
          <h2 className="display-3 mt-4">Put These Numbers In Front Of A Funder</h2>
          <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">
            The link carries the county, the acreage and the location exactly as you have set them, so whoever
            opens it sees this page, not a default. The PDF is the same figures on our letterhead.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <ShareCard
              title={`${county}: ${data.acres} acres priced six ways`}
              text={`${county} land, ${data.acres} acres, ${data.location_label.toLowerCase()} — freehold ${ksh(data.columns[0]?.freehold_shillings)}, or a 15-year lease from ${ksh(data.columns[0]?.rows.find((r) => r.term === "lease_15")?.day_one_shillings)}.`}
              card={`${BACKEND}/api/card/lease/${slug(county)}-${data.acres}-${location}.png`}
              path={`/lease-comparison?${query}`}
              label="Share this comparison"
              testId="lease-share"
            />
            <a
              href={`${BACKEND}/api/lease-comparison.pdf?${query}`}
              target="_blank"
              rel="noreferrer"
              data-testid="lease-share-pdf"
              className="mono inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-300 transition-colors hover:border-ochre hover:text-ochre"
            >
              <Download size={13} /> The same figures as a PDF
            </a>
          </div>
        </section>

        <section className="mt-20" data-testid="lease-land-lead">
          <p className="eyebrow">If the land is yours</p>
          <h2 className="display-3 mt-4">File The Exact Plot</h2>
          <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">
            If you are looking at these numbers because you hold the land, file it here. It goes into the same
            queue we take to partners, with the {county} figures on this page already attached — no forms to
            retype. A full proposal with documents goes through <Link to="/apply" className="text-ochre hover:text-white">the application</Link>.
          </p>
          <div className="mt-8 max-w-4xl">
            <LandLead
              county={county}
              acres={data.acres}
              location={location}
              locationLabel={data.location_label}
              figures={data.columns[0]
                ? `Freehold ${ksh(data.columns[0].freehold_shillings)} · per acre ${ksh(data.columns[0].per_acre_shillings)} · ground rent ${ksh(data.columns[0].rows.find((r) => r.term === "lease_99")?.rent_year_shillings)} a year`
                : ""}
            />
          </div>
        </section>

        <SavedComparisons
          current={{ county, county_b: countyB, acres: data.acres, location, location_label: data.location_label }}
          onOpen={(s) => {
            setCounty(s.county);
            setCountyB(s.county_b || "");
            setAcres(String(s.acres));
            setLocation(s.location);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />

        <LandLeadMap />

        <LandAlerts county={county} />

        <section className="mt-20 max-w-3xl" data-testid="lease-method">
          <p className="eyebrow">How this is worked out</p>
          <h2 className="display-3 mt-4">The Method, Attached</h2>
          <p className="mt-6 text-sm leading-relaxed text-zinc-400">{data.method}</p>
          <p className="mt-5 text-sm leading-relaxed text-zinc-500">{data.caveat}</p>
          <div className="mono mt-8 flex flex-wrap gap-4 text-[0.55rem] uppercase tracking-[0.16em]">
            <Link to="/apply" className="text-ochre hover:text-white" data-testid="lease-to-apply">Bring your county a project</Link>
            <Link to="/land-audit" className="text-ochre hover:text-white" data-testid="lease-to-land-audit">County land audit</Link>
            <Link to="/pitch-sizing" className="text-ochre hover:text-white" data-testid="lease-to-pitch-sizing">What fits on the land</Link>
            <Link to="/site-appraisal" className="text-ochre hover:text-white" data-testid="lease-to-appraisal">Appraise a specific plot</Link>
            <Link to={`/land-value/${county.toLowerCase().replace(/\s+/g, "-")}`} className="text-ochre hover:text-white" data-testid="lease-to-land-value">What an acre is worth here</Link>
            <Link to="/land-comparables" className="text-ochre hover:text-white" data-testid="lease-to-comparables">Check us against real deals</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
